import React, { useState } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, CheckCircle } from 'lucide-react';
import { products } from '../data/mock';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const product = products.find(p => p.slug === slug);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedFinish, setSelectedFinish] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedThickness, setSelectedThickness] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [added, setAdded] = useState(false);

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  const handleAddToCart = () => {
    if (!selectedFinish || !selectedSize || !selectedThickness) {
      alert('Please select Finish, Size, and Thickness before adding to cart.');
      return;
    }
    addToCart({
      productId: product.id,
      productName: product.name,
      slug: product.slug,
      image: product.images[0],
      finish: selectedFinish,
      size: selectedSize,
      thickness: selectedThickness,
      quantity,
      pricePerSqFt: product.pricePerSqFt,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const SelectGroup = ({ label, options, value, onChange }) => (
    <div>
      <span className="text-sm text-gray-500 block mb-2">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 text-sm rounded border transition-all ${
              value === opt
                ? 'border-[#111111] bg-[#111111] text-white'
                : 'border-gray-300 text-gray-700 hover:border-gray-500'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-20">
      {/* Back Button */}
      <div className="bg-[#FAFAF8] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center text-sm text-gray-600 hover:text-[#111111] transition-colors">
            <ArrowLeft size={16} className="mr-2" />
            Back to Products
          </Link>
        </div>
      </div>

      {/* Product Details */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images */}
            <div>
              <div className="aspect-square mb-4 overflow-hidden rounded-lg">
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="grid grid-cols-4 gap-4">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square rounded overflow-hidden border-2 transition-all ${
                      selectedImage === index ? 'border-[#111111]' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img src={image} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Details */}
            <div>
              <h1 className="text-4xl font-bold text-[#111111] mb-2">{product.name}</h1>
              <div className="flex gap-2 mb-4">
                <Badge variant="secondary">{product.colorCategory}</Badge>
                <Badge variant="outline">Origin: {product.origin}</Badge>
              </div>

              {/* Price */}
              <div className="mb-4">
                <span className="text-2xl font-semibold text-[#111111]">₹{product.pricePerSqFt}</span>
                <span className="text-gray-500 text-sm ml-1">/ sq ft</span>
              </div>

              <p className="text-base text-gray-600 mb-6 leading-relaxed">{product.description}</p>

              {/* Order Configuration */}
              <Card className="mb-6">
                <CardContent className="p-6 space-y-5">
                  <h3 className="font-semibold text-lg text-[#111111]">Configure Your Order</h3>

                  <SelectGroup
                    label="Finish *"
                    options={product.finishes}
                    value={selectedFinish}
                    onChange={setSelectedFinish}
                  />
                  <SelectGroup
                    label="Slab Size *"
                    options={product.slabSizes}
                    value={selectedSize}
                    onChange={setSelectedSize}
                  />
                  <SelectGroup
                    label="Thickness *"
                    options={product.thickness}
                    value={selectedThickness}
                    onChange={setSelectedThickness}
                  />

                  {/* Quantity */}
                  <div>
                    <span className="text-sm text-gray-500 block mb-2">Quantity (sq ft)</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setQuantity(q => Math.max(1, q - 10))}
                        className="w-9 h-9 rounded border border-gray-300 flex items-center justify-center text-lg hover:border-gray-500 transition"
                      >
                        −
                      </button>
                      <span className="w-16 text-center font-semibold text-[#111111]">{quantity}</span>
                      <button
                        onClick={() => setQuantity(q => q + 10)}
                        className="w-9 h-9 rounded border border-gray-300 flex items-center justify-center text-lg hover:border-gray-500 transition"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Subtotal */}
                  <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
                    <span className="text-sm text-gray-500">Subtotal</span>
                    <span className="text-lg font-bold text-[#111111]">
                      ₹{(product.pricePerSqFt * quantity).toLocaleString('en-IN')}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Add to Cart */}
              <Button
                size="lg"
                className="w-full bg-[#111111] text-white hover:bg-[#2a2a2a] mb-3"
                onClick={handleAddToCart}
              >
                {added ? (
                  <>
                    <CheckCircle size={18} className="mr-2" />
                    Added to Cart
                  </>
                ) : (
                  <>
                    <ShoppingCart size={18} className="mr-2" />
                    Add to Cart
                  </>
                )}
              </Button>

              <div className="flex gap-3">
                <Link to="/contact" className="flex-1">
                  <Button size="lg" variant="outline" className="w-full">
                    Request Sample
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specs */}
      <section className="py-10 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-semibold text-xl mb-6 text-[#111111]">Specifications</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-5 space-y-4">
                <div>
                  <span className="text-sm text-gray-500 block mb-2">Available Finishes</span>
                  <div className="flex flex-wrap gap-2">
                    {product.finishes.map(f => <Badge key={f} variant="outline">{f}</Badge>)}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block mb-2">Slab Sizes</span>
                  <div className="flex flex-wrap gap-2">
                    {product.slabSizes.map(s => <Badge key={s} variant="outline">{s}</Badge>)}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5 space-y-4">
                <div>
                  <span className="text-sm text-gray-500 block mb-2">Thickness Options</span>
                  <div className="flex flex-wrap gap-2">
                    {product.thickness.map(t => <Badge key={t} variant="outline">{t}</Badge>)}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block mb-2">Applications</span>
                  <div className="flex flex-wrap gap-2">
                    {product.applications.map(a => <Badge key={a} variant="secondary">{a}</Badge>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2 text-[#111111]">Export Ready</h4>
                <p className="text-sm text-gray-600">Container-load supply with FOB/CIF terms. Proper packaging and documentation included.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2 text-[#111111]">Quality Assured</h4>
                <p className="text-sm text-gray-600">Multi-stage quality control ensures consistent grading across all shipments.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2 text-[#111111]">Global Shipping</h4>
                <p className="text-sm text-gray-600">Worldwide delivery to USA, Europe, Middle East, and Southeast Asia markets.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
